import React from 'react';
import { Line } from 'react-chartjs-2';

import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns'; // needed for the TimeScale

import {
  Chart as ChartJS,
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
} from 'chart.js';

import { drawLinePlugin } from '@UI-Elements/ChartPlugins/DrawLinePlugin';
import { handleMouseDown, handleMouseMove, handleMouseUp } from '@UI-Elements/ChartPlugins/ChartUtils';
import { IndexRoutes } from './OpportunityUtils';

ChartJS.register(
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
);

const chartAreaBorder = {
  id: 'chartAreaBorder',
  borderColor: 'rgba(0, 0, 0, 0.1)',
  borderWidth: 1,
  borderDashOffset: 1,
  beforeDraw: (chart: any) => {
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = chartAreaBorder.borderColor;
    ctx.lineWidth = chartAreaBorder.borderWidth;
    ctx.lineDashOffset = chartAreaBorder.borderDashOffset;
    ctx.strokeRect(chartArea.left, chartArea.top, chartArea.width, chartArea.height);
    ctx.restore();
  },
};

const getOptions = (onDataClick: (_dataIndex: number) => void, minIndex: number): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: false,

    title: { display: false },
    tooltip: { mode: 'index', intersect: false },
    drawCurrentDateLine: true,
    drawLinePlugin: { minIndex },
    zoom: {
      zoom: {
        wheel: {
          enabled: true,
        },
        pinch: {
          enabled: true,
        },
        mode: 'xy',
      },
      pan: {
        enabled: false,
        mode: 'xy',
      },
    },
  } as any,
  onHover: (event: any) => {
    if (event.type === 'click') {
      const activeElements = event.chart.tooltip?.getActiveElements();
      if (activeElements?.length) {
        const {
          index: dataIndex,
          element: { x },
        } = activeElements[0];
        const topY = event.chart.scales.y.top;
        const bottomY = event.chart.scales.y.bottom;
        const clickedData = { dataIndex, topY, bottomY, x };
        event.chart.clicked = clickedData;
        onDataClick(clickedData.dataIndex);
      }
    }
  },
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'month' as const,
        displayFormats: { month: 'MM/yy' },
      },
      title: {
        display: false,
        text: 'Month',
      },
      ticks: { display: true, autoSkip: true },
      grid: { display: true },
    },
    y: {
      beginAtZero: false,
      offset: true,
      title: { display: true, text: `Material Price (USD)` },
    },
  },
  elements: { line: { tension: 0.2 }, point: { radius: 0 } },
});

const getData = (
  materialRoutes: any[],
  showRoutes: { routeId: string; routeName: string; color: string; visible: boolean }[],
  materialColor: string,
  selectedRoute: string,
): ChartData<'line'> => ({
  labels: materialRoutes.map((r) => r.date), //priceData.map((d) => dateString(d.date)),
  datasets: [
    {
      label: `Material Price`,
      data: materialRoutes.map((d) => ({
        x: new Date(d.date).getTime(),
        y: d.price,
      })),
      borderColor: materialColor,
      backgroundColor: 'rgba(53, 162, 235, 0.5)',
      fill: false,
      borderWidth: 4,
    },
    ...showRoutes
      .filter(({ visible }) => visible)
      .map(({ routeId, color, routeName }) => ({
        label: routeName,
        data: materialRoutes.map((d) => ({ x: new Date(d.date).getTime(), y: d[routeId] || undefined })),
        borderColor: color || 'rgba(103, 153, 224,1)',
        backgroundColor: 'rgba(53, 162, 235, 0)',
        borderWidth: routeId === selectedRoute ? 4 : 2,
      })),
    {
      label: 'Price estimate',
      data: materialRoutes.map((d) => ({
        x: new Date(d.date).getTime(),
        y: d.futurePrice,
      })),
      borderColor: materialColor,
      backgroundColor: 'rgba(53, 162, 235, 0.5)',
      fill: false,
      borderDash: [5, 5],
      borderWidth: 2,
    },
    // {
    //   label: 'Cost',
    //   data: materialRoutes.map((d) => ({ x: new Date(d.date).getTime(), y: d.cost })),
    //   borderColor: 'rgba(229, 20, 20, 0.5)',
    //   backgroundColor: 'rgba(229, 20, 20, 0.5)',
    //   fill: false,
    //   borderDash: [3, 3],
    //   borderWidth: 2,
    // },
  ],
});

interface Props {
  indexRoutesData: IndexRoutes[];
  showRoutes: { routeId: string; routeName: string; color: string; visible: boolean }[];
  onDataClick: (_priceData: IndexRoutes) => void;
  materialColor: string;
  selectedRoute: string;
}
export const OpportunityChart: React.FC<Props> = ({
  indexRoutesData,
  onDataClick,
  showRoutes,
  materialColor,
  selectedRoute,
}) => {
  React.useEffect(() => {
    //@ts-expect-error-type-not-match
    const chart = chartRef.current as ChartJS<'line'>;
    if (chart?.canvas) {
      chart.canvas.addEventListener('mousedown', handleMouseDown(chart));
      chart.canvas.addEventListener('mousemove', handleMouseMove(chart));
      chart.canvas.addEventListener('mouseup', handleMouseUp(chart));
    }

    return () => {
      if (chart?.canvas) {
        chart.canvas.removeEventListener('mousemove', handleMouseMove(chart));
        chart.canvas.removeEventListener('mousedown', handleMouseDown(chart));
        chart.canvas.removeEventListener('mouseup', handleMouseUp(chart));
      }
    };
  }, []);

  const chartRef = React.useRef();
  const innerClick = (dIndex: number) => {
    onDataClick(indexRoutesData[dIndex]);
  };
  return (
    <div className="flex-1 h-full  ">
      <Line
        options={getOptions(innerClick, indexRoutesData.length - 12)}
        data={getData(indexRoutesData, showRoutes, materialColor, selectedRoute)}
        plugins={[drawLinePlugin, chartAreaBorder]} //,drawCurrentDateLine]}
        ref={chartRef}
      />
    </div>
  );
};
